import 'rxjs/add/operator/map';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/observable/of';
import { Observable } from 'rxjs/Observable';
import { TypedEpic } from 'redux-observable';
import core, { App, GQL, Entities } from '@gin-betting/core';
import favorite, { Favorite } from '@gin-betting/favorite';

import { State } from 'types';
import ui from 'modules/ui';

type FavoriteToggleAction = ReturnType<
    App.TypedActionCreator<FavoriteToggleActionPayload>
>;

type FavoriteToggleActionPayload =
    | Favorite.ToggleFavoriteActionPayload
    | Favorite.FavoriteActionPayloadAddRemove;

const {
    actions: { getFavoriteEventsIds },
    epics: { requestLocalSport },
    selectors: { selectFavoriteIds },
    constants: {
        ADD_REHYDRATE_FAVORITE_MATCHES_SUCCESS,
        ADD_FAVORITE_MATCH_LOCAL,
        ADD_FAVORITE_MATCH_SUCCESS,
        REMOVE_FAVORITE_MATCH_LOCAL,
        REMOVE_FAVORITE_MATCH_SUCCESS,
    },
} = favorite;

const {
    utils: { getBettingOptions },
    entities: {
        constants: { MATCH_ENDED, DELETE_MATCH_ID },
        selectors: { selectMatch },
    },
} = core;

const {
    actions: { changeFavoriteCount },
    selectors: { selectOutrightsFavoriteCount, selectUpcomingFavoriteCount },
} = ui;

const favoriteCountTypeMapping = {
    [GQL.SportEventType.MATCH]: {
        type: 'upcoming' as const,
        selector: selectUpcomingFavoriteCount,
    },
    [GQL.SportEventType.OUTRIGHT]: {
        type: 'outrights' as const,
        selector: selectOutrightsFavoriteCount,
    },
};

export { requestLocalSport };

export const requestSport: TypedEpic<
    Favorite.ListTypeT,
    ReturnType<typeof getFavoriteEventsIds>
> = actions$ => {
    return actions$.ofType(ADD_REHYDRATE_FAVORITE_MATCHES_SUCCESS).map(() => {
        const { sportIds } = getBettingOptions();

        return getFavoriteEventsIds({
            sportIds,
            matchStatuses: [
                GQL.SportEventStatus.LIVE,
                GQL.SportEventStatus.NOT_STARTED,
                GQL.SportEventStatus.SUSPENDED,
                GQL.SportEventStatus.ENDED,
                GQL.SportEventStatus.CLOSED,
                GQL.SportEventStatus.CANCELLED,
            ],
        });
    });
};

type OutputAction = ReturnType<typeof changeFavoriteCount> | never;

export const decreaseFavoriteCount: TypedEpic<
    FavoriteToggleAction,
    OutputAction
> = (actions$, store) => {
    return actions$
        .ofType(ADD_FAVORITE_MATCH_LOCAL, ADD_FAVORITE_MATCH_SUCCESS)
        .switchMap(({ payload }) =>
            changeCountOnFavoriteToggle(store.getState(), payload, 1)
        );
};

export const increaseFavoriteCount: TypedEpic<
    FavoriteToggleAction,
    OutputAction
> = (actions$, store) => {
    return actions$
        .ofType(REMOVE_FAVORITE_MATCH_LOCAL, REMOVE_FAVORITE_MATCH_SUCCESS)
        .switchMap(({ payload }) =>
            changeCountOnFavoriteToggle(store.getState(), payload, -1)
        );
};

export const decreaseUpcomingFavoriteCountOnMatchEnd: TypedEpic<
    App.TypedAction<Entities.DeleteMatchIdArgs>,
    OutputAction
> = (actions$, store) => {
    return actions$
        .ofType(MATCH_ENDED, DELETE_MATCH_ID)
        .switchMap(({ payload }) => {
            const state = store.getState();
            const favoriteIds = selectFavoriteIds(state);

            if (!favoriteIds.includes(payload.matchId))
                return Observable.empty();

            const match = selectMatch(state, {
                matchId: payload.matchId,
            });

            if (!match) return Observable.empty();

            const { type, selector } = favoriteCountTypeMapping[match.type];

            const currentCount = selector(state);

            if (currentCount === null) return Observable.empty();

            return Observable.of(
                changeFavoriteCount({
                    type,
                    count: currentCount - 1,
                })
            );
        });
};

function changeCountOnFavoriteToggle(
    state: State,
    payload: FavoriteToggleActionPayload,
    delta: 1 | -1
): Observable<OutputAction> {
    let favoriteId = '';

    if (isApiTogglePayload(payload)) {
        const firstFavoriteId = payload.favoriteIds[0];

        if (firstFavoriteId) {
            favoriteId = firstFavoriteId;
        }
    } else {
        if (payload.favoriteId) {
            favoriteId = payload.favoriteId;
        }
    }

    const match = selectMatch(state, {
        matchId: favoriteId,
    });

    if (
        !match ||
        ![
            GQL.SportEventStatus.NOT_STARTED,
            GQL.SportEventStatus.SUSPENDED,
            GQL.SportEventStatus.LIVE,
        ].includes(match.status)
    ) {
        return Observable.empty();
    }

    const { type, selector } = favoriteCountTypeMapping[match.type];

    const currentCount = selector(state);

    if (currentCount === null) return Observable.empty();

    return Observable.of(
        changeFavoriteCount({
            type,
            count: currentCount + delta,
        })
    );
}

function isApiTogglePayload(
    payload: FavoriteToggleActionPayload
): payload is Favorite.FavoriteActionPayloadAddRemove {
    const { favoriteIds } = payload as Favorite.FavoriteActionPayloadAddRemove;

    return Array.isArray(favoriteIds);
}
